import { phoneInputUtils } from '@libs/ui/ds'
import type { PhoneInputValue } from '@libs/ui/ds'
import { useOnboardingTranslation } from '@libs/utils/translations'
import type { TFunction } from 'react-i18next'
import { z } from 'zod'

const createBaseAccountUserInfoSchema = (tFunction?: TFunction) => {
  return z.object({
    firstName: z
      .string()
      .trim()
      .min(1, tFunction?.('onboarding.qualification.profile.firstname.error')),
    lastName: z
      .string()
      .trim()
      .min(1, tFunction?.('onboarding.qualification.profile.lastname.error')),
    companyName: z
      .string()
      .trim()
      .min(1, tFunction?.('onboarding.qualification.profile.company.error')),
    jobTitle: z
      .string()
      .trim()
      .min(1, tFunction?.('onboarding.qualification.profile.job_title.error')),
    phone: z
      .object({
        countryCode: z
          .string()
          .trim()
          .min(1, tFunction?.('onboarding.common.forms.validation.required')),
        number: z
          .string()
          .trim()
          .min(1, tFunction?.('onboarding.common.forms.validation.required'))
      })
      .refine(
        data => {
          return phoneInputUtils.isValid({
            countryCode: data.countryCode,
            number: data.number
          } as PhoneInputValue)
        },
        {
          message: tFunction?.(
            'onboarding.common.forms.validation.invalidPhoneNumber'
          )
        }
      )
  })
}

// Base schema without translations (for validation purposes)
export const baseAccountUserInfoSchema = createBaseAccountUserInfoSchema()

export const useProfileQualificationSchema = () => {
  const { t } = useOnboardingTranslation()
  return createBaseAccountUserInfoSchema(t)
}

export type AccountUserInfoFormType = z.infer<typeof baseAccountUserInfoSchema>

export const isAccountUserInfoValid = (
  profile: unknown
): profile is AccountUserInfoFormType => {
  return baseAccountUserInfoSchema.safeParse(profile).success
}
